import { z } from 'zod';
import { RowLabelOptionalSchema } from './row-label';

export const HarvestTemplateSchema = z.object({
  id: z.number().int().positive(),
  company_id: z.string().uuid(),
  year: z.string().max(4).nullable().optional(),
  block_name: z.string().max(300).nullable().optional(),
  block_type: z.string().max(80).nullable().optional(),
  reference: z.string().max(100).nullable().optional(),
  crop: z.string().max(120).nullable().optional(),
  crop_variety: z.string().max(150).nullable().optional(),
  start_time: z.string().datetime().nullable().optional(),
  finish_time: z.string().datetime().nullable().optional(),
  start_row: RowLabelOptionalSchema,
  finish_row: RowLabelOptionalSchema,
  labour_id: z.string().nullable().optional(),
  assigned_user: z.string().nullable().optional(),
  task_status: z.string().max(100).nullable().optional(),
  harvest_complete: z.number().int().nullable().optional(),
  count: z.number().nullable().optional(), // Allow decimals (e.g., 123.5 units picked)
  week: z.number().int().nullable().optional(),
  is_manual_entry: z.coerce.number().int().min(0).max(1).nullable().optional(),
  staff_count: z.coerce.number().int().nullable().optional(),
  line_row_number: z.string().max(100).nullable().optional(),
  block_id: z.number().int().nullable().optional(),
  section_id: z.number().int().nullable().optional(),
  date_time: z.string().nullable().optional(),
  pack_category: z.string().max(150).nullable().optional(),
  pack_type: z.string().max(150).nullable().optional(),
  harvest_images: z.string().nullable().optional(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
  created_by: z.string().nullable().optional(),
  updated_by: z.string().nullable().optional(),
});

export const HarvestTemplateCreateSchema = HarvestTemplateSchema.omit({
  id: true,
  company_id: true,
  created_at: true,
  updated_at: true,
});

export const HarvestTemplateUpdateSchema = HarvestTemplateCreateSchema.partial();

export const HarvestTemplateListQuerySchema = z.object({
  search: z.string().optional(),
  block_type: z.string().optional(),
  page: z.coerce.number().int().positive().optional().default(1),
  per_page: z.coerce.number().int().positive().max(100).optional().default(25),
});

// Timer start endpoint schema
export const HarvestTemplateStartTimerSchema = z.object({
  block_id: z.number().int().positive('Block ID is required'),
  labour_ids: z.array(z.number().int().positive()).min(1, 'At least one labour member is required'),
  is_manual_entry: z.number().int().min(0).max(1).default(0),
  pack_category: z.string().max(150).optional(),
  pack_type: z.string().max(150).optional(),
  week: z.number().int().optional(),
  year: z.string().max(4).optional(),
});

// Timer stop endpoint schema
export const HarvestTemplateStopTimerSchema = z.object({
  finish_time: z.string().datetime('Invalid finish time format'),
  count: z.number().positive('Count must be greater than 0'),
  pack_category: z.string().max(150).optional(),
  pack_type: z.string().max(150).optional(),
  harvest_images: z.string().nullable().optional(),
});

// Image upload response
export const ImageUploadResponseSchema = z.object({
  success: z.boolean(),
  filename: z.string(),
  path: z.string(),
  message: z.string(),
});

export type HarvestTemplate = z.infer<typeof HarvestTemplateSchema>;
export type HarvestTemplateCreate = z.infer<typeof HarvestTemplateCreateSchema>;
export type HarvestTemplateUpdate = z.infer<typeof HarvestTemplateUpdateSchema>;
export type HarvestTemplateListQuery = z.infer<typeof HarvestTemplateListQuerySchema>;
export type HarvestTemplateStartTimer = z.infer<typeof HarvestTemplateStartTimerSchema>;
export type HarvestTemplateStopTimer = z.infer<typeof HarvestTemplateStopTimerSchema>;
export type ImageUploadResponse = z.infer<typeof ImageUploadResponseSchema>;
